import type { VendorBill } from '@/types'

/** Plain-language vendor payment status, derived from the vendor's own bills.
 *
 *  Deliberately NOT derived from the `vendor-aging` buckets. That endpoint
 *  buckets on `CURRENT_DATE - due_date` and files everything with
 *  `days_overdue <= 30` into `b_0_30` — so a bill due next week and a bill
 *  thirty days late land in the same column. Reading the due date per bill is
 *  both simpler and honest about which one it is. */
export type VendorTone = 'danger' | 'warning' | 'success' | 'info' | 'neutral'

export interface VendorStatus {
  tone: VendorTone
  text: string
  /** Positive = days past due. Used for sorting most-urgent-first. */
  daysLate: number
}

const DAY_MS = 24 * 60 * 60 * 1000

function daysBetweenTodayAnd(iso: string): number | null {
  const due = new Date(iso + 'T00:00:00')
  if (Number.isNaN(due.getTime())) return null
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((today.getTime() - due.getTime()) / DAY_MS)
}

export function isUnpaid(b: VendorBill): boolean {
  return (b.status === 'open' || b.status === 'partially_paid') && b.outstanding > 0.01
}

export function vendorStatusFromBills(bills: VendorBill[]): VendorStatus {
  const unpaid = bills.filter(isUnpaid)
  const drafts = bills.filter(b => b.status === 'draft')

  if (unpaid.length === 0) {
    // A draft bill is stock we received that nobody has checked against the
    // vendor's invoice yet — nothing is owed on paper, but it isn't "settled"
    // either, and saying so is what stops it being forgotten.
    if (drafts.length > 0) {
      return {
        tone: 'info',
        text: drafts.length === 1 ? '1 bill to confirm' : `${drafts.length} bills to confirm`,
        daysLate: -9998,
      }
    }
    return { tone: 'success', text: 'All settled', daysLate: -9999 }
  }

  let worst: number | null = null
  for (const b of unpaid) {
    if (!b.due_date) continue
    const d = daysBetweenTodayAnd(b.due_date)
    if (d == null) continue
    if (worst == null || d > worst) worst = d
  }

  if (worst == null) return { tone: 'warning', text: 'No due date set', daysLate: 0 }
  if (worst > 0) {
    return { tone: 'danger', text: worst === 1 ? '1 day late' : `${worst} days late`, daysLate: worst }
  }
  if (worst === 0) return { tone: 'warning', text: 'Due today', daysLate: 0 }
  const inDays = -worst
  return {
    tone: inDays <= 7 ? 'warning' : 'neutral',
    text: inDays === 1 ? 'Due tomorrow' : `Due in ${inDays} days`,
    daysLate: worst,
  }
}

/** Total still owed on a vendor's unpaid + draft bills. */
export function vendorOwed(bills: VendorBill[]): number {
  return bills.reduce((sum, b) => (b.status === 'void' || b.status === 'paid' ? sum : sum + b.outstanding), 0)
}

/**
 * Net position with a vendor (F-MM-N21): open bills less unused supplier
 * credits (returns refunded as a credit note). Positive = you owe them,
 * negative = they owe you. This is exactly what the Accounting ledger's
 * running balance ends on, so the headline can never argue with it again.
 */
export function vendorNetOwed(bills: VendorBill[], creditBalance = 0): number {
  return vendorOwed(bills) - Math.max(0, creditBalance || 0)
}

/** Headline copy for a net position: what to print, and the one-line reason. */
export function describeNetOwed(net: number, fmt: (n: number) => string): { value: string; sub: string } {
  if (net > 0.01) return { value: fmt(net), sub: 'as of today' }
  if (net < -0.01) return { value: 'Nothing', sub: `${fmt(-net)} credit with them` }
  return { value: 'Nothing', sub: 'fully settled' }
}

/** Bills whose due date has passed and still carry a balance. */
export function isOverdue(b: VendorBill): boolean {
  if (!isUnpaid(b) || !b.due_date) return false
  const d = daysBetweenTodayAnd(b.due_date)
  return d != null && d > 0
}

/** Bills falling due within the next 7 days (not yet late). */
export function isDueThisWeek(b: VendorBill): boolean {
  if (!isUnpaid(b) || !b.due_date) return false
  const d = daysBetweenTodayAnd(b.due_date)
  return d != null && d <= 0 && d >= -7
}
